import { useState, useEffect } from 'react'
import { NavLink, Link, useNavigate } from 'react-router-dom'
import Button from '../ui/Button'
import { BUSINESS } from '../../constants/business'
import { useAuth } from '../../contexts/AuthContext'
import { useCart } from '../../contexts/CartContext'

const PUBLIC_NAV = []

const ALWAYS_NAV = []

function TireLookupNav() {
  const { user } = useAuth()
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  function handleSubmit(e) {
    e.preventDefault()
    const q = query.trim()
    if (!q) { navigate('/tire-viewer?tab=size'); return }
    navigate(`/tire-viewer?tab=size&size=${encodeURIComponent(q)}`)
    setQuery('')
  }
  return (
    <div className="flex items-center gap-2">
      {!user && <button onClick={() => navigate('/tire-viewer?tab=size')} className="text-sm font-bold text-white whitespace-nowrap hidden xl:block hover:text-amber-200 transition-colors cursor-pointer">Free Tire Lookup</button>}
      <div className="flex items-center bg-white/[0.12] rounded-xl overflow-hidden border border-white/20">
      <button
        onClick={() => navigate('/tire-viewer?tab=vehicle')}
        className={`w-24 text-center py-2 text-[13px] font-semibold transition-all rounded-lg cursor-pointer ${window.location.pathname === '/tire-viewer' && (!window.location.search || window.location.search.includes('tab=vehicle')) ? 'bg-white text-brand-charcoal' : 'text-amber-300 hover:text-white hover:bg-white/10'}`}
      >
        By Vehicle
      </button>
      <span className="w-px h-5 bg-white/20" />
      <button
        onClick={() => navigate('/tire-viewer?tab=size')}
        className={`w-24 text-center py-2 text-[13px] font-semibold transition-all rounded-lg cursor-pointer ${window.location.pathname === '/tire-viewer' && window.location.search.includes('tab=size') ? 'bg-white text-brand-charcoal' : 'text-amber-300 hover:text-white hover:bg-white/10'}`}
      >
        By Size
      </button>
      <span className="w-px h-5 bg-white/20" />
      <form onSubmit={handleSubmit} className="flex items-center">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Size..."
            className="w-40 xl:w-52 pl-8 pr-3 py-2 text-[13px] bg-transparent text-white/80 placeholder-white/25 focus:outline-none focus:placeholder-white/40 focus:text-white transition-all"
          />
        </div>
      </form>
      </div>
    </div>
  )
}

const AUTH_NAV = [
  { href: '/orders', label: 'My Orders' },
]


export default function Navbar() {
  const { user, logout } = useAuth()
  const { cartCount } = useCart()
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  function handleLogout() {
    logout()
    navigate('/')
  }

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && mobileOpen) setMobileOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [mobileOpen])

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  const handleAnchorClick = (href) => {
    setMobileOpen(false)
    if (href.startsWith('/#')) {
      const id = href.slice(2)
      const el = document.getElementById(id)
      if (el) {
        // Already on home page — smooth scroll
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 80)
      } else {
        // On another page — navigate to home then scroll
        window.location.href = href
      }
    }
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-brand-charcoal shadow-lg'
          : 'bg-brand-charcoal'
      }`}
    >
      <nav
        role="navigation"
        aria-label="Main navigation"
        className="container-custom h-[88px] flex items-center justify-between"
      >
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center rounded-lg px-2 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red"
          aria-label={`${BUSINESS.name} — Home`}
          onClick={() => setMobileOpen(false)}
        >
          <img
            src="/photos/fasttrack-logo.png"
            alt={BUSINESS.name}
            className="h-[72px] w-auto object-contain bg-white/90 rounded-lg px-4 py-2"
          />
        </Link>

        {/* Desktop Nav — 3 columns: left links | center tire lookup | right auth */}
        <div className="hidden lg:flex items-center flex-1">
          {/* Left: spacer */}
          <div className="flex items-center gap-1" />

          {/* Center: tire lookup */}
          <div className="flex-1 flex justify-center">
            <TireLookupNav />
          </div>

          {/* Divider */}
          <span className="text-white/20 select-none mx-2">|</span>

          {/* Auth controls */}
          {user ? (
            <>
              <NavLink to="/orders" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors rounded ${isActive ? 'text-white' : 'text-brand-silver hover:text-white'}`}>
                My Orders
              </NavLink>
              {user.roles?.includes('fasttrackadmin') && (
                <NavLink to="/admin" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors rounded ${isActive ? 'text-white' : 'text-brand-silver hover:text-white'}`}>
                  Admin
                </NavLink>
              )}
              <Link
                to="/cart"
                className="relative ml-1 p-2 text-brand-silver hover:text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red rounded"
                aria-label={`Cart${cartCount > 0 ? `, ${cartCount} items` : ''}`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61H19a2 2 0 001.99-1.81L22 7H6" />
                </svg>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-red text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>
              <Link
                to="/dashboard"
                className="flex items-center gap-2 ml-2 text-brand-silver hover:text-white text-sm font-medium transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-brand-red flex items-center justify-center text-white text-xs font-bold">
                  {user.name?.charAt(0).toUpperCase() || '?'}
                </span>
                {user.name?.split(' ')[0]}
              </Link>
              <button
                onClick={handleLogout}
                className="ml-2 px-3 py-2 text-brand-silver hover:text-white text-sm font-medium transition-colors rounded"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="px-3 py-2 text-brand-silver hover:text-white text-sm font-medium transition-colors rounded">
                Login
              </Link>
              <span className="text-white/20 select-none mx-1">|</span>
              <Button
                as="a"
                href={`tel:${BUSINESS.phone}`}
                variant="cta-phone"
                size="sm"
                className="ml-1"
                aria-label={`Call ${BUSINESS.phoneFormatted}`}
              >
                {BUSINESS.phoneFormatted}
              </Button>
            </>
          )}
        </div>

        {/* Mobile: Phone icon + Hamburger */}
        <div className="flex items-center gap-2 lg:hidden">
          {!user && <a
            href={`tel:${BUSINESS.phone}`}
            className="w-11 h-11 flex items-center justify-center text-brand-silver hover:text-white transition-colors rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red"
            aria-label={`Call ${BUSINESS.phoneFormatted}`}
          >
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.86 13a19.79 19.79 0 01-3.07-8.67A2 2 0 012.77 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.91 9.91a16 16 0 006.16 6.16l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
            </svg>
          </a>}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="w-11 h-11 flex flex-col items-center justify-center gap-1.5 text-white rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            <span className={`block h-0.5 w-6 bg-current transition-all duration-200 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block h-0.5 w-6 bg-current transition-all duration-200 ${mobileOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-0.5 w-6 bg-current transition-all duration-200 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div
        id="mobile-menu"
        role="region"
        aria-label="Mobile navigation"
        className={`lg:hidden bg-brand-charcoal border-t border-white/10 overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="container-custom py-4 flex flex-col gap-1">
          {/* Tire Lookup section */}
          <div className="px-4 py-2">
            {!user && <button onClick={() => { navigate('/tire-viewer?tab=size'); setMobileOpen(false) }} className="text-sm font-bold text-white/80 mb-3 text-left hover:text-amber-300 transition-colors">Free Tire Look Up →</button>}
            <div className="flex gap-2 mb-3">
              <Link to="/tire-viewer?tab=vehicle" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2.5 text-amber-400 hover:text-amber-300 bg-white/5 hover:bg-white/10 font-body font-semibold text-sm rounded-lg border border-white/10 transition-colors">
                By Vehicle
              </Link>
              <Link to="/tire-viewer?tab=size" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-2.5 text-amber-400 hover:text-amber-300 bg-white/5 hover:bg-white/10 font-body font-semibold text-sm rounded-lg border border-white/10 transition-colors">
                By Size
              </Link>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); const v = e.target.elements.msize.value.trim(); if (v) { navigate(`/tire-viewer?tab=size&size=${encodeURIComponent(v)}`); setMobileOpen(false) } }}>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input name="msize" type="text" placeholder="Search tire size..." className="w-full pl-9 pr-3 py-2.5 text-sm bg-white/10 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-amber-500" />
              </div>
            </form>
          </div>
          <div className="mx-4 border-t border-white/10 my-1" />
          {/* Other nav links */}
          {[...(user ? AUTH_NAV : []), ...(user?.roles?.includes('fasttrackadmin') ? [{ href: '/admin', label: 'Admin' }] : [])].map(({ href, label }) => (
            <NavLink
              key={href}
              to={href}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `px-4 py-3 font-body font-medium text-base rounded transition-colors ${
                  isActive ? 'text-white bg-white/10' : 'text-brand-silver hover:text-white hover:bg-white/5'
                }`
              }
            >
              {label}
            </NavLink>
          ))}

          {/* Mobile CTA */}
          <div className="pt-3 mt-1 border-t border-white/10 flex flex-col gap-2">
            {user ? (
              <>
                <span className="px-4 py-3 text-brand-silver font-body font-medium text-base flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-brand-red flex items-center justify-center text-white text-xs font-bold">
                    {user.name?.charAt(0).toUpperCase() || '?'}
                  </span>
                  {user.name?.split(' ')[0]}
                </span>
                <Link
                  to="/cart"
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 text-brand-silver hover:text-white hover:bg-white/5 font-body font-medium text-base rounded transition-colors flex items-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61H19a2 2 0 001.99-1.81L22 7H6" />
                  </svg>
                  Cart
                  {cartCount > 0 && (
                    <span className="ml-auto bg-brand-red text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                      {cartCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/orders"
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 text-brand-silver hover:text-white hover:bg-white/5 font-body font-medium text-base rounded transition-colors"
                >
                  My Orders
                </Link>
                <button
                  onClick={() => { setMobileOpen(false); handleLogout() }}
                  className="px-4 py-3 text-brand-silver hover:text-white hover:bg-white/5 font-body font-medium text-base rounded transition-colors text-left"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="px-4 py-3 text-brand-silver hover:text-white hover:bg-white/5 font-body font-medium text-base rounded transition-colors"
              >
                Login
              </Link>
            )}
            {!user && (
              <Button
                as="a"
                href={`tel:${BUSINESS.phone}`}
                variant="cta-phone"
                size="md"
                className="w-full justify-center"
                aria-label={`Call us at ${BUSINESS.phoneFormatted}`}
              >
                Call {BUSINESS.phoneFormatted}
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
